import { createContext, useContext } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { empresasService } from "app/services";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Spinner } from "app/components";

const TelefonosContext = createContext();

export function TelefonosProvider({ children }) {
  const params = useParams();
  const navigate = useNavigate();

  const telefonosQuery = useQuery({
    queryKey: ["empresa:get:telefonos", params.empresa_id],
    queryFn: () =>
      empresasService.getTelefonos({ empresaId: params.empresa_id }),
  });

  const telefonoAddMutation = useMutation({
    queryKey: ["empresa:add:telefono", params.empresa_id],
    mutationFn: (data) =>
      toast.promise(empresasService.addTelefono(data), {
        loading: "Agregando el número de teléfono...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await telefonosQuery.refetch();
    },
  });

  const telefonoUpdateMutation = useMutation({
    queryKey: ["empresa:update:telefono"],
    mutationFn: (data) =>
      toast.promise(empresasService.updateTelefono(data), {
        loading: "Actualizando el número de teléfono...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await telefonosQuery.refetch();
    },
  });

  const telefonoDeleteMutation = useMutation({
    queryKey: ["empresa:delete:telefono"],
    mutationFn: (data) =>
      toast.promise(empresasService.deleteTelefono(data), {
        loading: "Eliminando el número de teléfono...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await telefonosQuery.refetch();
    },
  });

  const telefonoVerifyMutation = useMutation({
    mutationKey: ["empresa:verify:telefono"],
    mutationFn: (data) =>
      toast.promise(empresasService.verifyTelefono(data), {
        loading: "Verificando el número de teléfono...",
        success: (response) => response.statusText,
        error: (error) => error.response.statusText,
      }),
    onSuccess: async () => {
      navigate(".");

      return await telefonosQuery.refetch();
    },
  });

  async function addTelefono({ telefono }) {
    return await telefonoAddMutation.mutateAsync({
      empresaId: params.empresa_id,
      telefono,
    });
  }

  async function updateTelefono({ telefonoId, telefono }) {
    const { telefono_verificado, ...rest } = telefono;

    return await telefonoUpdateMutation.mutateAsync({
      empresaId: params.empresa_id,
      telefonoId,
      telefono: rest,
    });
  }

  async function deleteTelefono({ telefonoId }) {
    const deleted = window.confirm("¿Desea eliminar este número de teléfono?");

    if (!!deleted) {
      await telefonoDeleteMutation.mutateAsync({
        empresaId: params.empresa_id,
        telefonoId: telefonoId,
      });

      return true;
    }

    return false;
  }

  async function verifyTelefono({ telefonoId }) {
    const confirmed = window.confirm(
      "¿Desea verificar este número de teléfono?"
    );

    if (confirmed) {
      return await telefonoVerifyMutation.mutateAsync({
        empresaId: params.empresa_id,
        telefonoId,
      });
    }

    return false;
  }

  async function cancel() {
    if (window.confirm("¿Desea cancelar la edición?")) {
      return navigate(".");
    }
  }

  if (!!telefonosQuery.isLoading) {
    return <Spinner label="Cargando los números de teléfono..." />;
  } else if (!!telefonosQuery.isError) {
    return <ErrorMessage message={telefonosQuery.error.response.statusText} />;
  }

  return (
    <TelefonosContext.Provider
      value={{
        telefonos: telefonosQuery.data,
        isLoading: telefonosQuery.isLoading,
        isFetching: telefonosQuery.isFetching,
        isRefetching: telefonosQuery.isRefetching,
        addTelefono,
        isAdding: telefonoAddMutation.isLoading,
        updateTelefono,
        isUpdating: telefonoUpdateMutation.isLoading,
        deleteTelefono,
        isDeleting: telefonoDeleteMutation.isLoading,
        verifyTelefono,
        isVerifing: telefonoVerifyMutation.isLoading,
        cancel,
      }}
    >
      {children}
    </TelefonosContext.Provider>
  );
}

export function useTelefonosContext() {
  const telefonosContext = useContext(TelefonosContext);

  if (!telefonosContext) {
    throw new Error(
      "useTelefonosContext should be used inside the TelefonosProvider."
    );
  }

  return telefonosContext;
}
